// @ts-strict-ignore
import { APP } from '../../../../config';
import { getIOSVersion, getIsApp } from '../../../../modules/AppInfo';
import nonCriticalException from '../../../../modules/exceptionLogger';
import fbAPI from '../../../../modules/fbAPI';
import {
  IShareParams,
  ShareChannelFactory,
  ShareChannelMap,
  ShareMessage,
  ShareTarget,
  ShareProvider,
  socialSharing,
  SocialSharingChannel,
} from '../../../../modules/share';
import { IS_MINE_MESSAGE } from '../constants';
import { providerAtSalon, getProfileShareImage } from './helpers';
import { getProviderLocation } from '../../../../modules/provider/getProviderLocation';

const IOS_VERSION = getIOSVersion();

// Note: Re-ported the following code to stay close to the original from socialShare.js
const handleShare = (
  params: IShareParams,
  onSuccess: () => void,
  onFail: (err: string) => void,
) => {
  const packageString: SocialSharingChannel = (
    IOS_VERSION
      ? SocialSharingChannel.FacebookiOS
      : SocialSharingChannel.FacebookAndroid
  );

  function shareViaFacebookApp() {
    /* Android will either share to installed FB app or use web-based FB
       share in-app as a backup option. */

    /* On iOS <12, FB share is native to the operating system - user will be asked to
       add their FB account to their system settings.
       On iOS >=12, native FB share has been removed and sharing must be done via installed
       FB app or web-based FB share */
    socialSharing().shareViaFacebook(
      `${params.text}: ${params.url}`,
      params.image as string,
      params.url as string,
      () => {
        if (onSuccess) {
          onSuccess();
        }
      },
      err => {
        nonCriticalException(err, {
          extra: 'shareViaFacebook failed',
        });
        if (onFail) {
          onFail(err);
        }
      },
    );
  }

  // Reference: https://developers.facebook.com/docs/sharing/reference/feed-dialog/v3.1#params
  function postToFeed() {
    const opts: Record<string, any> = {
      method: 'share',
      href: params.url,
      picture: params.image,
      quote: params.text,
      contentURL: params.url,
      imageURL: params.image,
      contentDescription: params.text,
      contentTitle: params.title,
      ref: 'FBSProfileShare',
    };

    if (APP && IOS_VERSION) {
      // this is the only option available for iOS App that makes it here
      opts.share_feedWeb = true;
    }
    fbAPI.showDialog(opts).then(() => {
      if (onSuccess) {
        onSuccess();
      }
    }, err => {
      nonCriticalException(err, {
        extra: 'shareViaFacebookConnect failed',
      });
      if (onFail) {
        onFail(err);
      }
    });
  }

  function shareViaFacebookConnect() {
    fbAPI.getLoginStatus().then(postToFeed, () => {
      fbAPI.login([
        'public_profile',
        'email',
      ]).then(postToFeed);
    });
  }

  const sharingPlugin = socialSharing();

  if (getIsApp() && sharingPlugin) {
    sharingPlugin.canShareVia(
      packageString,
      null,
      null,
      null,
      null,
      shareViaFacebookApp,
      shareViaFacebookConnect,
    );
  } else {
    shareViaFacebookConnect();
  }
};

type ShareText = { title: string; text: string };
const generateShareText = (target: ShareTarget, provider?: ShareProvider): ShareText => {
  if (target === ShareTarget.PLATFORM || !provider) {
    return {
      title: 'Discover & Book on StyleSeat | StyleSeat',
      text: 'Discover & Book online with StyleSeat | StyleSeat',
    };
  }

  const location_city = getProviderLocation(provider)?.city;
  const text = location_city
    ? `Book an appointment online with ${providerAtSalon(provider)} in ${location_city}`
    : `Book an appointment online with ${providerAtSalon(provider)}`;
  return {
    title: `Book an appointment online with ${provider.name} | StyleSeat`,
    text,
  };
};

export function profileShareParams(
  provider: ShareProvider,
  url: string,
  user: { provider_id?: number },
  message: ShareMessage = {},
): IShareParams {
  const isMine = user && provider?.id === user?.provider_id;
  const { title, text } = generateShareText(ShareTarget.PROFILE, provider);
  const image = getProfileShareImage(provider);

  return {
    title: message.title || title,
    text: message.text || (isMine ? IS_MINE_MESSAGE : text),
    // Don't share image if client is sharing a pro's profile
    image: isMine ? (message.image || image) : null,
    url: message.url || url,
  };
}

function platformShareParams(
  url: string,
  message: ShareMessage = {},
): IShareParams {
  const { title, text } = generateShareText(ShareTarget.PLATFORM, null);

  return {
    title: message.title || title,
    text: message.text || text,
    // Don't share image if client is sharing a pro's profile
    image: message.image || null,
    url: message.url || url,
  };
}

const profileShare = (provider, url, user, onSuccess, onFail, message = {}) => {
  const params = profileShareParams(provider, url, user, message);
  return () => {
    handleShare(params, onSuccess, onFail);
  };
};

const platformShare: ShareChannelFactory<null> = (
  _,
  url,
  __,
  onSuccess,
  onFail,
  message = {},
) => {
  const params = platformShareParams(url, message);

  return () => {
    handleShare(params, onSuccess, onFail);
  };
};

export default {
  [ShareTarget.PROFILE]: profileShare,
  [ShareTarget.SEARCH]: profileShare,
  [ShareTarget.PLATFORM]: platformShare,
} as ShareChannelMap;
